/**
 * List Input Rules Plugin for ProseMirror
 *
 * Provides markdown-style shortcuts for creating lists:
 * - "- ", "* ", or "+ " at line start → bullet list
 * - "1. " at line start → ordered list
 *
 * Features:
 * - Only activates when editor is editable
 * - Automatically joins adjacent lists of same type
 * - Removes the trigger text after conversion
 * - Works at start of empty or non-empty paragraphs
 */

import type { Plugin } from "@tiptap/pm/state";
import { PluginKey } from "@tiptap/pm/state";
import { inputRules, wrappingInputRule } from "@tiptap/pm/inputrules";
import { schema } from "../schema";

export const listInputRulesPluginKey = new PluginKey("listInputRules");

/**
 * Creates a plugin that enables markdown-style list shortcuts.
 *
 * Typing "- ", "* ", or "+ " at the start of a line creates a bullet list.
 * Typing "1. ", "2. ", etc. at the start of a line creates an ordered list.
 *
 * @returns Plugin that handles list input rules
 */
export function createListInputRulesPlugin(): Plugin {
  // Bullet list: "- ", "* ", or "+ " at start of line
  // Pattern: ^[-*+]\s$
  // - ^ = start of paragraph
  // - [-*+] = any of the three bullet markers
  // - \s = any whitespace (space, tab)
  // - $ = end of match (required by inputRules API)
  const bulletListRule = wrappingInputRule(
    /^[-*+]\s$/,
    schema.nodes.bullet_list
  );

  // Ordered list: "1. ", "2. ", etc. at start of line
  // Pattern: ^(\d+)\.\s$
  // - ^ = start of paragraph
  // - (\d+) = one or more digits (captured for order attribute)
  // - \. = literal dot
  // - \s = any whitespace
  // - $ = end of match (required by inputRules API)
  const orderedListRule = wrappingInputRule(
    /^(\d+)\.\s$/,
    schema.nodes.ordered_list,
    (match) => {
      const num = parseInt(match[1], 10);
      // Clamp to safe HTML5 range (HTML spec: max start value is 2147483647)
      // This prevents integer overflow and undefined browser behavior
      const order = Math.max(1, Math.min(num, 2147483647));
      return { order };
    }
  );

  return inputRules({
    rules: [bulletListRule, orderedListRule],
  });
}
